import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { inject, observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import { Button, Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import { Api } from 'services/Api';
import Toast from 'helpers/toaster';
import UserSidebar from './user-sidebar';

// Invite people. One field takes typed or pasted email addresses and turns
// them into people below, each with an optional name and a role.
// Styles: styles/invite-people.scss.
//
// The API creates all invitations or none (a failing row rolls the batch
// back), and refuses an email that already has an invitation, even an
// expired one. So people are checked against the team and existing
// invitations before sending, and anyone already invited gets a Resend.

// Same rule as the Invitation model's email format validation.
const EMAIL = /^[^@\s]+@(?:[-a-z0-9]+\.)+[a-z]{2,}$/i;
const EMAIL_IN_TEXT = /[^\s<>,;:"'()[\]]+@(?:[-a-z0-9]+\.)+[a-z]{2,}/i;

const ROLES = [
  { value: 'technician', label: 'Technician' },
  { value: 'admin', label: 'Admin' },
];

let nextKey = 1;
const newPerson = (fields = {}) => ({
  key: nextKey++,
  email: '',
  first_name: '',
  last_name: '',
  role: 'technician',
  ...fields,
});

const normalise = (email) => String(email || '').trim().toLowerCase();

const splitName = (name) => {
  const parts = String(name || '').replace(/["']/g, '').trim().split(/\s+/).filter(Boolean);
  return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
};

// "Ana Ruiz <ana@x.com>, bo@y.com" (commas, semicolons or new lines) into
// people. Pieces without an email address are returned as `leftover`.
export const parsePeople = (text, role) => {
  const people = [];
  const leftover = [];
  String(text || '')
    .split(/[\n,;]+/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .forEach((chunk) => {
      const match = chunk.match(EMAIL_IN_TEXT);
      if (!match) {
        leftover.push(chunk);
        return;
      }
      const name = chunk.replace(match[0], '').replace(/[<>]/g, '').trim();
      people.push(newPerson({ email: match[0], role, ...splitName(name) }));
    });
  return { people, leftover };
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const initialsFor = (person) => {
  const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  const source = name || person.email.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
};

const NEXT_STEPS = [
  { title: 'They get an email', text: 'Sent from FieldNexus with a link to join your team.' },
  { title: 'They set a password', text: 'Names they skip here can be filled in then.' },
  { title: 'They show up in Users', text: 'With the role you chose here.' },
];

@inject('viewStore', 'userStore') @observer
class InviteUsers extends Component {

  @observable people = [];
  @observable draft = '';
  @observable draftRole = 'technician';
  @observable draftNote = null;

  // Emails already on the team or invited, keyed by lower-cased email.
  @observable teamByEmail = new Map();
  @observable invitationByEmail = new Map();

  @observable serverErrors = new Map();
  @observable sendFailed = false;
  @observable sending = false;
  @observable sent = null;
  @observable resending = new Set();

  composerRef = React.createRef();

  componentDidMount() {
    this.loadExisting();
  }

  // Read-only lookups so problems show before sending. If they fail the
  // page still works; the API's own checks remain.
  loadExisting() {
    Api.post('/users/search', { role: 'all' })
      .then(action((response) => {
        const map = new Map();
        (response.data.data || []).forEach(({ attributes }) => {
          map.set(normalise(attributes.email), (attributes.full_name || '').trim() || attributes.email);
        });
        this.teamByEmail = map;
      }))
      .catch(() => {});
    Api.post('/users/search', { role: 'invitations' })
      .then(action((response) => {
        const map = new Map();
        (response.data.data || []).forEach(({ id, attributes }) => {
          if (attributes.status === 'accepted') return;
          map.set(normalise(attributes.email), {
            id: attributes.id || id,
            expired: Boolean(attributes.expires_at) && moment(attributes.expires_at).isBefore(moment()),
          });
        });
        this.invitationByEmail = map;
      }))
      .catch(() => {});
  }

  // --- checks ---------------------------------------------------------------

  // What's wrong with a person, or null. `kind` picks the treatment.
  problemFor(person, index) {
    const email = normalise(person.email);
    if (!email) return { kind: 'error', message: 'Add an email address.' };
    if (!EMAIL.test(email)) return { kind: 'error', message: 'Check this email address.' };
    const firstIndex = this.people.findIndex((other) => normalise(other.email) === email);
    if (firstIndex !== index) return { kind: 'error', message: 'Already in this list.' };
    if (this.teamByEmail.has(email)) {
      return { kind: 'info', message: `${this.teamByEmail.get(email)} is already on your team.` };
    }
    const invitation = this.invitationByEmail.get(email);
    if (invitation) {
      return {
        kind: 'invited',
        invitation,
        message: invitation.expired
          ? 'Invited before, but the link expired. Resend it to give them 3 more days.'
          : 'Already invited. Resend the email instead.',
      };
    }
    if (this.serverErrors.has(email)) return { kind: 'error', message: this.serverErrors.get(email) };
    return null;
  }

  @computed get problemCount() {
    return this.people.filter((person, index) => this.problemFor(person, index)).length;
  }

  @computed get canSend() {
    return this.people.length > 0 && this.problemCount === 0 && !this.sending;
  }

  // --- composer ---------------------------------------------------------------

  // Adds whatever addresses `text` holds; anything else stays in the field.
  @action addFrom(text) {
    const { people, leftover } = parsePeople(text, this.draftRole);
    this.people = [...this.people, ...people];
    this.draft = leftover.join(', ');
    if (people.length === 0 && leftover.length > 0) {
      this.draftNote = 'That doesn\'t look like an email address.';
    } else if (leftover.length > 0) {
      this.draftNote = `Added ${plural(people.length, 'person', 'people')}. The rest has no email address, so it's still in the field.`;
    } else {
      this.draftNote = people.length > 1 ? `Added ${people.length} people.` : null;
    }
    this.sendFailed = false;
    return people.length;
  }

  onDraftChange = (event) => {
    const { value } = event.target;
    this.draftNote = null;
    // Typing a comma or semicolon after an address adds it.
    if (/[,;]\s*$/.test(value) && EMAIL_IN_TEXT.test(value)) {
      this.addFrom(value);
    } else {
      this.draft = value;
    }
  };

  onDraftKeyDown = (event) => {
    if (event.key === 'Enter' && this.draft.trim()) {
      event.preventDefault();
      this.addFrom(this.draft);
    } else if (event.key === 'Backspace' && !this.draft && this.people.length) {
      // Like a tag field: backspace in an empty field takes back the last one.
      this.removePerson(this.people[this.people.length - 1]);
    }
  };

  // A pasted list becomes people at once.
  onDraftPaste = (event) => {
    const text = event.clipboardData && event.clipboardData.getData('text');
    if (!text || !/[\n,;]/.test(text.trim())) return;
    event.preventDefault();
    this.addFrom(`${this.draft} ${text}`);
  };

  // --- people ---------------------------------------------------------------

  @action setField(person, field, value) {
    if (field === 'email') {
      this.serverErrors.delete(normalise(person.email));
      this.sendFailed = false;
    }
    person[field] = value;
  }

  @action removePerson(person) {
    this.people = this.people.filter((other) => other.key !== person.key);
  }

  resend(person, invitation) {
    const email = normalise(person.email);
    this.resending.add(email);
    this.props.userStore.resendUserInvite(invitation.id)
      .then(action(() => {
        this.invitationByEmail.set(email, { ...invitation, expired: false });
        this.removePerson(person);
        Toast.show({ icon: 'envelope', message: `Invitation resent to ${person.email.trim()}`, intent: Intent.SUCCESS });
      }))
      .catch(() => {
        Toast.show({ icon: 'warning-sign', message: `Couldn't resend to ${person.email.trim()}. Try again.`, intent: Intent.DANGER });
      })
      .then(action(() => this.resending.delete(email)));
  }

  // --- send -----------------------------------------------------------------

  send = () => {
    if (!this.canSend) return;
    const invitations = this.people.map((person) => ({
      email: person.email.trim(),
      first_name: person.first_name.trim(),
      last_name: person.last_name.trim(),
      role: person.role,
    }));
    this.sending = true;
    this.sendFailed = false;
    this.props.userStore.sendInvitations(invitations)
      .then(action(() => {
        this.sent = invitations;
        this.people = [];
        this.draft = '';
        this.draftNote = null;
        this.loadExisting();
      }))
      .catch(action((error) => {
        const failed = (error && error.response && error.response.data && error.response.data.failed) || [];
        const errors = new Map();
        failed.forEach((failure) => {
          const message = Array.isArray(failure.errors) ? failure.errors.join(', ') : failure.errors;
          errors.set(normalise(failure.email), message || 'This invitation couldn\'t be created.');
        });
        this.serverErrors = errors;
        this.sendFailed = true;
      }))
      .then(action(() => { this.sending = false; }));
  };

  @action inviteMore = () => {
    this.sent = null;
    setTimeout(() => this.composerRef.current && this.composerRef.current.focus(), 0);
  };

  openInvitations = () => {
    const { viewStore } = this.props;
    viewStore.userSearch = { ...viewStore.userSearch, section: 'invitations', term: '' };
    viewStore.users();
  };

  leave = () => {
    const { viewStore } = this.props;
    if (viewStore.inviteOnboarding) viewStore.dashboard();
    else viewStore.users();
  };

  // --- render -----------------------------------------------------------------

  renderRoleToggle(value, onChange, label, size) {
    return (
      <div className={`invite__roles${size ? ` invite__roles--${size}` : ''}`} role="radiogroup" aria-label={label}>
        {ROLES.map((role) => (
          <button
            key={role.value}
            type="button"
            role="radio"
            aria-checked={value === role.value}
            className={`invite__role${value === role.value ? ' is-selected' : ''}`}
            onClick={() => onChange(role.value)}
          >
            {role.label}
          </button>
        ))}
      </div>
    );
  }

  renderComposer() {
    return (
      <div className="invite__composer">
        <label htmlFor="invite-composer" className="invite__composer-label">
          Email addresses
        </label>
        <div className="invite__composer-field">
          <Icon icon="envelope" iconSize={18} className="invite__composer-icon" />
          <input
            id="invite-composer"
            ref={this.composerRef}
            className="invite__composer-input"
            type="text"
            inputMode="email"
            autoComplete="off"
            autoFocus={true}
            placeholder="Type or paste email addresses"
            aria-describedby="invite-composer-hint"
            value={this.draft}
            onChange={this.onDraftChange}
            onKeyDown={this.onDraftKeyDown}
            onPaste={this.onDraftPaste}
          />
          <div className="invite__composer-actions">
            <span className="invite__composer-as" aria-hidden="true">as</span>
            {this.renderRoleToggle(this.draftRole, (role) => { this.draftRole = role; }, 'Role for people you add')}
            <Button
              intent={Intent.PRIMARY}
              className="invite__composer-add"
              disabled={!this.draft.trim()}
              onClick={() => this.addFrom(this.draft)}
            >
              Add
            </Button>
          </div>
        </div>
        <p id="invite-composer-hint" className={`invite__composer-hint${this.draftNote ? ' is-note' : ''}`} role="status">
          {this.draftNote || 'Press Enter or a comma after each address. Paste a list from a spreadsheet or email to add everyone at once.'}
        </p>
      </div>
    );
  }

  renderProblem(person, problem) {
    const email = normalise(person.email);
    return (
      <div className={`invite__note invite__note--${problem.kind}`}>
        <Icon icon={problem.kind === 'error' ? 'error' : 'info-sign'} iconSize={13} />
        <span className="invite__note-text">{problem.message}</span>
        {problem.kind === 'invited' && (
          <Button
            small={true}
            minimal={true}
            icon="envelope"
            loading={this.resending.has(email)}
            onClick={() => this.resend(person, problem.invitation)}
          >
            Resend
          </Button>
        )}
      </div>
    );
  }

  renderPeople() {
    if (this.people.length === 0) {
      return (
        <div className="invite__empty">
          <span className="invite__empty-avatars" aria-hidden="true">
            <span /><span /><span />
          </span>
          <p>People you add show up here. Names are optional.</p>
        </div>
      );
    }
    return (
      <ul className="invite__people" aria-label="People to invite">
        {this.people.map((person, index) => {
          const problem = this.problemFor(person, index);
          const label = person.email.trim() || `person ${index + 1}`;
          return (
            <li key={person.key} className={`invite__person${problem ? ` has-${problem.kind}` : ''}`}>
              <div className="invite__person-row">
                <span className={`invite__avatar invite__avatar--${person.role}`} aria-hidden="true">
                  {initialsFor(person)}
                </span>
                <input
                  type="email"
                  className="invite__quiet invite__quiet--email"
                  aria-label={`Email for ${label}`}
                  aria-invalid={Boolean(problem && problem.kind === 'error')}
                  value={person.email}
                  onChange={(event) => this.setField(person, 'email', event.target.value)}
                />
                <input
                  type="text"
                  className="invite__quiet"
                  placeholder="First name"
                  aria-label={`First name for ${label}`}
                  value={person.first_name}
                  onChange={(event) => this.setField(person, 'first_name', event.target.value)}
                />
                <input
                  type="text"
                  className="invite__quiet"
                  placeholder="Last name"
                  aria-label={`Last name for ${label}`}
                  value={person.last_name}
                  onChange={(event) => this.setField(person, 'last_name', event.target.value)}
                />
                {this.renderRoleToggle(person.role, (role) => this.setField(person, 'role', role), `Role for ${label}`, 'small')}
                <Button
                  minimal={true}
                  small={true}
                  icon="cross"
                  className="invite__remove"
                  aria-label={`Remove ${label}`}
                  onClick={() => this.removePerson(person)}
                />
              </div>
              {problem && this.renderProblem(person, problem)}
            </li>
          );
        })}
      </ul>
    );
  }

  renderStatus() {
    if (this.people.length === 0) return 'Add at least one email address.';
    if (this.problemCount > 0) {
      return `${plural(this.problemCount, 'person needs', 'people need')} a look before you send.`;
    }
    return `${plural(this.people.length, 'person', 'people')} will get an email.`;
  }

  renderForm() {
    const { viewStore } = this.props;
    const count = this.people.length;
    return (
      <section className="invite__panel" aria-label="People to invite">
        {this.renderComposer()}
        {this.renderPeople()}

        {this.sendFailed && (
          <div className="invite__alert" role="alert">
            <Icon icon="error" iconSize={16} />
            <span>
              {this.serverErrors.size > 0
                ? 'Nothing was sent. Fix the highlighted people, then send again.'
                : 'Nothing was sent because the server didn\'t respond. Try again.'}
            </span>
          </div>
        )}

        <footer className="invite__footer">
          <span className="invite__status" role="status">{this.renderStatus()}</span>
          <div className="invite__footer-actions">
            <Button minimal={true} onClick={this.leave}>
              {viewStore.inviteOnboarding ? 'Skip for now' : 'Cancel'}
            </Button>
            <Button
              intent={Intent.PRIMARY}
              icon="send-message"
              className="invite__send"
              loading={this.sending}
              disabled={!this.canSend}
              onClick={this.send}
            >
              {count > 1 ? `Send ${count} invitations` : 'Send invitation'}
            </Button>
          </div>
        </footer>
      </section>
    );
  }

  renderSent() {
    const { viewStore } = this.props;
    return (
      <section className="invite__panel invite__done" aria-live="polite">
        <span className="invite__done-icon" aria-hidden="true"><Icon icon="tick" iconSize={22} /></span>
        <h2 className="invite__done-title">
          {this.sent.length === 1 ? 'Invitation sent' : `${this.sent.length} invitations sent`}
        </h2>
        <p className="invite__done-text">
          Links work for 7 days. You can resend or cancel them from Invitations.
        </p>
        <ul className="invite__sent">
          {this.sent.map((person) => {
            const name = [person.first_name, person.last_name].filter(Boolean).join(' ');
            return (
              <li key={person.email}>
                <span className={`invite__avatar invite__avatar--${person.role}`} aria-hidden="true">
                  {initialsFor(person)}
                </span>
                <span className="invite__sent-text">
                  <span className="invite__sent-name">{name || person.email}</span>
                  {name && <span className="invite__muted">{person.email}</span>}
                </span>
                <span className="invite__sent-role">{person.role === 'admin' ? 'Admin' : 'Technician'}</span>
              </li>
            );
          })}
        </ul>
        <div className="invite__done-actions">
          <Button icon="plus" onClick={this.inviteMore}>Invite more people</Button>
          <Button intent={Intent.PRIMARY} onClick={viewStore.inviteOnboarding ? () => viewStore.dashboard() : this.openInvitations}>
            {viewStore.inviteOnboarding ? 'Go to dashboard' : 'View invitations'}
          </Button>
        </div>
      </section>
    );
  }

  render() {
    return (
      <div className="user-page-layout">
        <Helmet>
          <title>Invite people | FieldNexus</title>
        </Helmet>
        <div className="row no-gutters">
          <div className="col-lg-2 sidebar-responsive">
            <UserSidebar />
          </div>
          <div className="col-lg-10">
            <div className="invite">
              <div className="invite__main">
                <header className="invite__header">
                  <h1 className="invite__title">Invite people</h1>
                  <p className="invite__description">
                    Add your technicians and office staff. Each person gets an email with a link to join your team.
                  </p>
                </header>
                {this.sent ? this.renderSent() : this.renderForm()}
              </div>

              <aside className="invite__aside" aria-labelledby="invite-next-title">
                <h2 id="invite-next-title" className="invite__aside-title">What happens next</h2>
                <ol className="invite__steps">
                  {NEXT_STEPS.map((step, index) => (
                    <li key={step.title} className="invite__step">
                      <span className="invite__step-number" aria-hidden="true">{index + 1}</span>
                      <span>
                        <span className="invite__step-title">{step.title}</span>
                        <span className="invite__step-text">{step.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="invite__aside-note">
                  Links work for 7 days. You can resend or cancel them from Invitations.
                </p>
              </aside>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default InviteUsers;
